/*

Usage Notes

///
    Grid will support both static data sets and paged data fetched from server
    If getData is provided, the grid will fetch data from the server
    If data is provided, the grid will use that data
///
Ref:
- getData: expose method to parent to trigger fetching data
- resetPaging: expose method to parent to reset paging (note this will also trigger fetching data)

Parameters:
- features: {
    pageable?: boolean | GridPagerSettings | undefined,
    sortable?: boolean,
    filterable?: boolean,
    exportToPDF?: boolean,
    exportToExcel?: boolean,
    reorderColumns?: boolean,
    simpleSearch?: boolean

}
    features to enable in the grid
    pageable: enable paging
    sortable: enable sorting
    filterable: enable filtering
    exportToPDF: enable export to PDF
    exportToExcel: enable export to Excel
    simpleSearch: enable simple search
- data (any[])
    array of data to display in the grid
- columns (ICustomColumn[])
    array of columns to display in the grid
- defaultSort: (SortDescriptor[])
    default sort to use for the table if one not in local storage
- getData: (props: GridGetDataProps) => Promise<GetDataResponse>
    function to get data from the server

Example:
<PagedSortedGrid
    ref={gridRef}
    features={{ pageable: true, sortable: true, filterable: true, exportToPDF: true, exportToExcel: true }}
    data={data}
    columns={columns}
    defaultSort={[{ field: 'firstField', dir: 'asc' }]}
/>
*/
import { toast } from 'core/toast'
import React, { ComponentType, forwardRef, ReactElement, ReactNode, useCallback, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react'
import { Grid, GridPageChangeEvent, GridColumn, GridSortChangeEvent, GridRowClickEvent, GridCellProps, GridFilterChangeEvent, GridPagerSettings, GridToolbar, GridDetailRowProps, GridExpandChangeEvent, GridSelectionChangeEvent, GridHeaderSelectionChangeEvent, GridSelectableSettings, GridHeaderCellProps, GridRowProps } from '@progress/kendo-react-grid'
import { CompositeFilterDescriptor, SortDescriptor, filterBy, orderBy } from '@progress/kendo-data-query'
import { GridPDFExport } from '@progress/kendo-react-pdf'
import { GridCellEnum, GridItemDeleteEnum, ICustomColumn, IDropDownValue, IGridLocalStorageNames, IMultiSelect, IPagedData } from 'types'
import { Button } from 'react-bootstrap'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'state/store'
import { Skeleton } from '@progress/kendo-react-indicators'
import { DEFAULT_PERMISSION_MESSAGE } from 'core/consts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ConfirmModal from 'components/Modals/ConfirmModal'
import { getErrors, useStateRef } from 'core/helpers'
import { Field, Form, FormElement } from '@progress/kendo-react-form'
import { Checkbox, Input } from '@progress/kendo-react-inputs'
import { getSelectedState } from '@progress/kendo-react-data-tools'
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'
import Spinner from 'components/Spinner/Spinner'
import { Col, Row } from 'react-bootstrap'
import { title } from 'process'

export interface ISAGridRef {
    fetch: () => void
    resetAndFetch: () => void
}

interface IProps {

    features: {
        pageable?: boolean | GridPagerSettings | undefined,
        sortable?: boolean,
        filterable?: boolean,
        exportToPDF?: boolean,
        exportToExcel?: boolean,
        reorderColumns?: boolean,
        simpleSearch?: boolean,
        title?: string,
        actions?: boolean
        resizable?: boolean
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any[],
    columns: ICustomColumn[],
    defaultSort: SortDescriptor[],
    onRowClick?: Function,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getData?: (props: GridGetDataProps) => Promise<GetDataResponse>,
    deleteAction?: {
        handler: Function,
        canDelete: boolean,
        deleteTitle?: string,
        getDeleteMessage?: Function
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getItemDeleteType?: (item: any) => GridItemDeleteEnum | undefined
    },
    action?: {
        items: Array<IDropDownValue>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handler: (event: DropDownListChangeEvent, ids: number[], selectedData?: any[] | undefined) => void,
        default: IDropDownValue | undefined,
        sendSelectedData?: boolean,
        popupWidth?: string
    },
    detail?: null | ComponentType<GridDetailRowProps>,
    identifierPropertyName?: string | undefined,
    getPreSelectedIds?: Function,
    renderRow?: ((row: ReactElement<HTMLTableRowElement>, props: GridRowProps, selectedIds: number[]) => ReactNode) | undefined
    localStorageNames?: IGridLocalStorageNames
}

interface PageState {
    skip: number
    take: number
    total?: number
    currentPage?: number
}

export interface GetDataResponse {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[]
    total: number
    page?: number
    pageSize?: number
    skip?: number
}

export interface GridGetDataProps {
    paging?: PageState
    filter?: CompositeFilterDescriptor | undefined
    search?: string
    sortBy?: string
    sortAscending?: boolean
}

export interface IGridSelectProps {
    onSelectionChange?: ((event: GridSelectionChangeEvent) => void) | undefined,
    onHeaderSelectionChange?: ((event: GridHeaderSelectionChangeEvent | Function) => void) | undefined,
    selectable?: GridSelectableSettings,
    selectedField?: string,
    dataItemKey?: string
}

const SAGrid = forwardRef<ISAGridRef, IProps>((props, ref) => {
    const { features, data, columns, defaultSort, onRowClick, getData, deleteAction, detail, identifierPropertyName, getPreSelectedIds, action, renderRow, localStorageNames } = props
    const dispatch = useDispatch<AppDispatch>()
    const initialDataState: PageState =
    {
        skip: 0,
        take: localStorageNames ? parseInt(localStorage.getItem(localStorageNames.pageSize) ?? '25') : 25,
        currentPage: localStorageNames && localStorage.getItem(localStorageNames!.selectedPage) ? parseInt(localStorage.getItem(localStorageNames!.selectedPage)!) : 1
    }

    const [loaded, setLoaded] = useState(false)
    const [page, setPage] = useState<PageState>(initialDataState)
    const [total, setTotal] = useState<number>(data ? data.length : 0)
    const abortControllerRef = useRef<AbortController | null>(null)
    const requestCounterRef = useRef(0)

    const [gridData, setGridData] = useState(data || [])

    const [_, forceUpdate] = useReducer((x) => x + 1, 0)

    // Simple Search
    const searchFormRef = useRef<null>(null)
    const [hasSearched, setHasSearched] = useState(false)


    //Local Storage
    //sort
    const [sort, setSort] = useState<Array<SortDescriptor>>(localStorageNames && localStorage.getItem(localStorageNames.sort) != null ? JSON.parse(localStorage.getItem(localStorageNames.sort)!) : defaultSort)
    // search input value
    const [searchInputValue, setSearchInputValue] = useState<string | null>(localStorageNames ? localStorage.getItem(localStorageNames.search) : '')

    const [selectedPage, setSelectedPage] = useState(() => {
        const storedSelectedPage = localStorageNames && parseInt(localStorage.getItem(localStorageNames!.selectedPage) ?? '1')
        return storedSelectedPage
    })
    //page size
    const [pageSize, setPageSize] = useState<number>(localStorageNames ? parseInt(localStorage.getItem(localStorageNames.pageSize) ?? '25') : 25)

    useEffect(() => {

        if (localStorageNames) {
            localStorage.setItem(localStorageNames.sort, JSON.stringify(!sort || sort.length == 0 ? defaultSort : sort))
            localStorage.setItem(localStorageNames.search, searchInputValue ? searchInputValue : '')
            localStorage.setItem(localStorageNames.selectedPage, JSON.stringify(selectedPage))
            localStorage.setItem(localStorageNames.pageSize, JSON.stringify(pageSize))
        }
    }, [sort, searchInputValue, selectedPage, pageSize])

    //Delete Single
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [itemToDelete, setItemToDelete] = useState<any>()
    const [deleteErrors, setDeleteErrors] = useState<Array<string>>([])
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [itemDeleted, setItemDeleted] = useState(false)

    // Filter
    // const initialFilter: CompositeFilterDescriptor = {
    //     logic: 'and',
    //     filters: [{ field: 'productName', operator: 'contains', value: 'CRM' }],
    // }
    const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined)

    //Export to PDF
    let gridPDFExport: GridPDFExport | null
    const exportPDF = () => {
        if (gridPDFExport) {
            gridPDFExport.save(evaluatedData(data ? data : gridData, true))
        }
    }

    //Export to Excel
    const _export = React.useRef<ExcelExport | null>(null)
    const exportExcel = () => {
        if (_export.current !== null) {
            _export.current.save(evaluatedData(data ? data : gridData, true))
        }
    }

    const rowClicked = (e: GridRowClickEvent) => {
        if (onRowClick)
            onRowClick(e)
    }

    const pageChange = (event: GridPageChangeEvent) => {
        const take = event.page.take
        setPageSize(take)
        setPage({
            ...event.page,
            take,
            currentPage: Math.floor(event.page.skip / take) + 1,
        })
        setIsNewPage(true)

    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function evaluatedData(newData: any[], allPages = false) {
        let evaluateddata = newData
        if (filter) {
            evaluateddata = filterBy(evaluateddata, filter)
        }
        if (sort) {
            evaluateddata = orderBy(evaluateddata, sort)
        }
        setTotal(evaluateddata.length)
        if (features.pageable && !allPages) {
            return evaluateddata.slice(page.skip, page.take + page.skip)
        }
        return evaluateddata
    }

    const checkValidPageNumber = async (totalItems: number | undefined, pageSize: number, page: number): Promise<number> => {
        const lastPage = totalItems === 0 ? 1 : Math.ceil((totalItems ?? 1) / pageSize)
        const pageNumber = lastPage < page ? lastPage : page
        setSelectedPage(pageNumber)
        return pageNumber
    }

    // call this method anytime page or pageSize changes
    const fetchData = useCallback(async (pageProp: PageState, filterProp: CompositeFilterDescriptor | undefined, sortProp: SortDescriptor[]) => {

        if (abortControllerRef.current)
            abortControllerRef.current.abort()

        abortControllerRef.current = new AbortController()
        const requestId = ++requestCounterRef.current

        try {
            // trigger the loading bar
            await dispatch(startLoadingAction())
            if (getData) {
                const sortBy = sortProp && sortProp.length > 0 ? sortProp[0].field : defaultSort[0].field

                const sortAscending = sortProp && sortProp.length > 0 ? sortProp[0].dir === 'asc' : defaultSort[0].dir === 'asc'

                const pagingWithTotal = { ...pageProp, total }

                const result = await getData({ paging: pagingWithTotal, filter: filterProp, sortBy: sortBy, sortAscending: sortAscending, search: searchInputValue ?? '' })

                if (requestId !== requestCounterRef.current)
                    return

                setTotal(result.total)

                let validPage = result.total && result.total > 1 && result.pageSize && result.page ? await checkValidPageNumber(result.total, result.pageSize, result.page) : pageProp.currentPage ?? 1
                if (result && result.page && result.pageSize && result.skip && validPage && validPage < result.page) {
                    setPage((prevState) => ({
                        ...prevState,
                        ...initialDataState
                    }))
                }

                setGridData(result.data)
                //Handle the multi select
                if (identifierPropertyName) {
                    //If the fetch was for something other than a new page, 
                    //then we need to create a new list of ids that only include the new items
                    if (!isNewPageRef.current) {
                        let data = result.data
                        let ids: IMultiSelect[] = data.map(item => ({ id: item[identifierPropertyName], selected: false, onCurrentPage: true, data: item }))
                        if (getPreSelectedIds) {
                            try {
                                const preSelectedIds: number[] = await getPreSelectedIds()
                                ids = ids.map(item => {
                                    if (preSelectedIds.includes(item.id)) {
                                        return { ...item, selected: true }
                                    }
                                    return item
                                })
                                //When the preselected ids are not on the current page, we need to add them to the list
                                preSelectedIds.forEach(id => {
                                    if (!ids.some(item => item.id === id)) {
                                        ids.push({ id: id, selected: true, onCurrentPage: false, data: undefined })
                                    }
                                })
                            } catch (e) {
                                console.error('Error getting preselected ids', e)
                            }

                        }
                        setNewIds(ids)
                    } else {
                        // Add any new items to the newIds list if they don't already exist
                        const newIdsArray = newIds.map(item => ({ ...item, onCurrentPage: false }))
                        result.data.forEach((item) => {
                            const itemId = item[identifierPropertyName]
                            const existingItem = newIdsArray.find((newItem: { id: number, selected: boolean }) => newItem.id === itemId)

                            if (!existingItem) {
                                newIdsArray.push({ id: itemId, selected: false, onCurrentPage: true, data: item })
                            } else {
                                // Item is on the current page, so set onCurrentPage to true
                                existingItem.onCurrentPage = true
                                // Add Data to the item if it doesn't already exist, can happen if item was created via getPreSelectedIds
                                if (existingItem.data === undefined) {
                                    existingItem.data = item
                                }
                            }
                        })
                        // Are all the new items already selected
                        const allSelected = result.data.every((item) => {
                            const itemId = item[identifierPropertyName]
                            const correspondingItem = newIdsArray.find((newItem: { id: number, selected: boolean }) => newItem.id === itemId)
                            return correspondingItem && correspondingItem.selected
                        })
                        setSelectAll(allSelected)
                        setNewIds(newIdsArray)
                    }
                }
                setIsNewPage(false)
                setLoaded(true)
            }
        }
        catch (e) {
            // toast.responseErrors(e)
        }
        finally {
            if (requestId === requestCounterRef.current) {
                //Maks sure cancel search button is displayed if search came from local storage
                if (searchInputValue != '')
                    setHasSearched(true)
                // stop the loading bar
                await dispatch(endLoadingAction())
            }
        }
    }, [searchInputValue, page.currentPage, page.take, filter, getData, dispatch])

    useImperativeHandle(ref, () => ({
        fetch: () => {
            //only allow fetching if the grid has already loaded for the first time
            if (loaded)
                fetchData(page, filter, sort)
        },
        resetAndFetch: () => setPage((prevState) => ({
            ...prevState,
            skip: 0,
            currentPage: 1
        }))
    }))

    useEffect(() => {
        if (getData) {
            fetchData(page, filter, sort)
        } else {
            setGridData(evaluatedData(data ? data : []))
        }

    }, [page, filter, sort, dispatch])

    useEffect(() => {
        setGridData(evaluatedData(data ? data : []))
    }, [data])

    /* =========================
        DELETE ACTION SECTION
    ========================= */

    const deleteItemMessage = (itemDeleteType: GridItemDeleteEnum | undefined) => {
        let cantDeleteMessage = undefined
        switch (itemDeleteType) {
            case GridItemDeleteEnum.CantBeDeletedDefaultRecord:
                cantDeleteMessage = 'This item is a default record used for all dealers, it cannot be deleted'
                break
            case GridItemDeleteEnum.CantBeDeletedInUse:
                cantDeleteMessage = 'This item is in use, it cannot be deleted'
                break
            case GridItemDeleteEnum.CantBeDeletedPermission:
                cantDeleteMessage = DEFAULT_PERMISSION_MESSAGE
                break
        }
        return cantDeleteMessage
    }

    // Custom Cell for Delete Action
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const DeleteActionCell: React.FC<GridCellProps> = (props) => {
        const { dataItem } = props

        if (!dataItem) return null

        if (deleteAction) {
            let cantDeleteMessage: string | undefined = undefined
            let deleteMessageType: GridItemDeleteEnum | undefined
            let title = 'Delete'

            if (deleteAction.getItemDeleteType) {
                deleteMessageType = deleteAction.getItemDeleteType(dataItem)
                cantDeleteMessage = deleteItemMessage(deleteMessageType)
                if (cantDeleteMessage) {
                    title = cantDeleteMessage
                }
            } else if (!deleteAction.canDelete) {
                title = DEFAULT_PERMISSION_MESSAGE
            }

            return (
                <td
                    className='text-center cursor-pointer'
                    onClick={(e) => e.stopPropagation()}
                >
                    <Button
                        className='gridButton btn btn-sm'
                        title={title}
                        onClick={(e) => showDeleteConfirmModal(e, dataItem, cantDeleteMessage, deleteMessageType!)}
                    >
                        <FontAwesomeIcon icon='trash' />
                    </Button>
                </td>
            )
        }

        return null
    }

    // user clicked delete button, so show the delete modal
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const showDeleteConfirmModal = async (event: React.MouseEvent<HTMLElement, MouseEvent>, item: any, cantDeleteMessage: string | undefined, deleteMessageType: GridItemDeleteEnum) => {
        if (deleteAction && deleteAction.canDelete || cantDeleteMessage) {

            if (!cantDeleteMessage) {
                setDeleteErrors([])
                setItemToDelete(item)
                setShowDeleteConfirm(true)
            } else {
                toast.error(cantDeleteMessage)
            }

        } else {
            toast.error(DEFAULT_PERMISSION_MESSAGE)
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deleteModalCancelled = async (item: any) => {
        setItemToDelete(undefined)
        setDeleteLoading(false)
        setShowDeleteConfirm(false)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deleteModalConfirmed = async (event: React.MouseEvent<HTMLElement, MouseEvent>, item: any) => {

        try {

            if (deleteAction) {
                setDeleteLoading(true)

                const message = await deleteAction.handler(item)
                await fetchData(page, filter, sort)
                setItemDeleted(!itemDeleted,) // Used to force the refresh of the data 
                setShowDeleteConfirm(false)
                if (message) {
                    toast.success(message)
                }

            } else {
                throw Error('Delete not supported')
            }
        }
        catch (e) {
            setDeleteErrors(getErrors(e))
        }
        finally {
            setDeleteLoading(false)
        }
    }
    /* ================
           MULTI SELECT
       =================== */
    const [selectAll, setSelectAll] = useState(false)
    const [isNewPage, setIsNewPage, isNewPageRef] = useStateRef(false)

    const toggleSelectAll = (selected: boolean) => {
        setSelectAll(selected)
        setNewIds(newIds.map(item => item.onCurrentPage ? { ...item, selected: selected } : item))
    }

    const SelectedCheckBoxHeaderCell: ComponentType<GridHeaderCellProps> = () => {
        const onHeaderCellClick = (e: { stopPropagation: () => void }) => {
            e.stopPropagation()
            toggleSelectAll(!selectAll)
        }

        return (
            <div className='text-center cursor-pointer' style={{ width: '40px' }} onClick={onHeaderCellClick}>
                <Checkbox checked={selectAll} onClick={onHeaderCellClick} />
            </div>)
    }
    const [newIds, setNewIds] = useState<Array<IMultiSelect>>([])
    const onMultiSelectCellClick = (id: number) => {
        if (newIds != undefined && newIds.length > 0) {
            const found = newIds.find(i => i.id === id)
            if (found) {
                found.selected = !found.selected
            }
        }
        forceUpdate()
    }
    // Custom Cell to support multi-row select
    const multiSelectCell = (props: GridCellProps) => {
        const field = props.field || ''
        let value = props.dataItem[field]
        const idSelect = newIds.find(i => i.id == value as number)


        const onCellClick = (e: { stopPropagation: () => void }) => {
            e.stopPropagation()
            onMultiSelectCellClick(value)

        }

        return (
            <td className='text-center cursor-pointer' style={{ width: '40px' }} onClick={onCellClick}>
                <Checkbox checked={idSelect ? idSelect.selected : false} />
            </td>
        )
    }

    const HeaderCellFromType = (cellType: GridCellEnum) => {
        switch (cellType) {
            case GridCellEnum.MultiDelete:
                return SelectedCheckBoxHeaderCell
            case GridCellEnum.MultiSelect:
                return SelectedCheckBoxHeaderCell
        }
    }

    const gridRenderRow = (row: ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
        if (renderRow) {
            return renderRow(row, props, newIds.filter(i => i.selected).map(i => i.id))
        }
        return row
    }

    /* ================
        SIMPLE SEARCH
    =================== */
    const handleSearchSubmit = (values: { [name: string]: string }, event?: React.SyntheticEvent<Event>) => {
        event?.preventDefault()
        setSearchInputValue(values.searchInputBox.toString())
        setHasSearched(true)
        setPage((prevState) => ({
            ...prevState,
            currentPage: 1
        }))
    }

    const clearClicked = () => {
        // if (searchFormRef.current)
        //     searchFormRef.current.resetForm() // TODO - Fix this
        setSearchInputValue('')
        setHasSearched(false)
        setPage((prevState) => ({
            ...prevState,
            currentPage: 1
        }))
    }

    const expandChange = (event: GridExpandChangeEvent) => {
        event.dataItem.expanded = event.value
        setGridData([...gridData]) //force re-render to show the detail row
        return null
    }
    const Actions = () => {
        return (action ? <DropDownList
            textField='text'
            dataItemKey='id'
            style={{ minWidth: '225px' }}
            popupSettings={action.popupWidth ? { width: action.popupWidth } : undefined}
            className='me-1'
            data={action.items}
            onChange={(e) => {
                action.handler(
                    e,
                    newIds.filter(item => item.selected).map(item => item.id),
                    action.sendSelectedData ? newIds.filter(item => item.selected).map(item => item.data) : undefined
                )
            }}
            value={action.default}
        /> : <></>)
    }

    const SimpleSearch = () => {
        return (
            <div className='gridActions'>
                <div className='d-column d-md-grid d-md-flex justify-content-start justify-content-md-end mb-2'>
                    <Form
                        ref={searchFormRef}
                        initialValues={{ searchInputBox: searchInputValue }}
                        key={JSON.stringify(searchInputValue)}
                        onSubmit={handleSearchSubmit}
                        render={(formRenderProps) => (
                            <FormElement
                                onReset={clearClicked}
                                onSubmit={formRenderProps.onSubmit}
                                noValidate>
                                <div className='d-flex searchWidth'>

                                    <Field
                                        name='searchInputBox'
                                        placeholder='Search'
                                        style={{ height: '36px' }}
                                        component={Input}
                                        className='mt-3 mt-md-0'
                                        value={searchInputValue}
                                    />

                                    <Button
                                        className='btn btn-primary ms-1 me-1 mt-3 mt-md-0'
                                        disabled={!formRenderProps.allowSubmit}
                                        type='submit'
                                        title='Search'>
                                        <FontAwesomeIcon icon='search' />
                                    </Button>
                                    {hasSearched &&
                                        <Button
                                            className='btn btn-default mt-3 mt-md-0'
                                            name='reset'
                                            title='Clear Search'
                                            type='reset'>
                                            <FontAwesomeIcon icon='times' />
                                        </Button>
                                    }

                                </div>
                            </FormElement>
                        )}
                    />
                </div>
            </div>
        )
    }

    /* ==============
        CELL TYPES
    ============== */

    const CellFromType = (cellType: GridCellEnum) => {
        switch (cellType) {
            case GridCellEnum.Concatenate:
                return ConcatenateCell
            case GridCellEnum.DeleteAction:
                return DeleteActionCell
            case GridCellEnum.MultiSelect:
                return multiSelectCell
            case GridCellEnum.YesNoBoolean:
                return YesNoBooleanCell
            default:
                return () => <td>Invalid Type</td>
        }
    }

    // Custom Cell to display only the first 50 characters
    const ConcatenateCell = (props: GridCellProps) => {
        const field = props.field || ''
        let value: string = props.dataItem[field]
        if (value && value.length > 85) {
            value = `${value.substring(0, 85)}...`
        }

        return (
            <td>{value}</td>
        )
    }

    // Custom Cell to display Yes/No instead of true/false
    const YesNoBooleanCell = (props: GridCellProps) => {
        const field = props.field || ''
        const value = props.dataItem[field]
        return (
            <td>{value ? 'Yes' : 'No'}</td>
        )
    }

    const grid = (
        <Grid
            className='gridWrap cursor-pointer noWrap'
            reorderable={features.reorderColumns}
            resizable={features.resizable}
            data={gridData}
            skip={features.pageable ? page.skip : undefined}
            take={features.pageable ? page.take : undefined}
            total={features.pageable ? total : undefined}
            pageable={features.pageable && features.pageable === true ? { pageSizes: [10, 25, 50, 100], buttonCount: 5 } : features.pageable}
            onPageChange={features.pageable ? pageChange : undefined}
            onRowClick={rowClicked}
            sortable={features.sortable}
            sort={sort}
            onSortChange={(e: GridSortChangeEvent) => {
                setSort(e.sort)
            }}
            filterable={features.filterable}
            filter={filter}
            onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
            detail={detail}
            expandField='expanded'
            onExpandChange={expandChange}
            rowRender={gridRenderRow}
        >
            {(features.exportToExcel != undefined && features.exportToExcel === true) || (features.exportToPDF != undefined && features.exportToPDF === true)
                ?
                <GridToolbar>
                    <Button
                        title='Export Excel'
                        type='button'
                        onClick={exportExcel}
                        hidden={features.exportToExcel === false || features.exportToExcel === undefined}
                    >
                        Export to Excel
                    </Button>

                    <Button
                        title='Export PDF'
                        type='button'
                        onClick={exportPDF}
                        hidden={features.exportToPDF === false || features.exportToPDF === undefined}
                    >
                        Export PDF
                    </Button>
                </GridToolbar>
                : null
            }

            {
                columns.map((column, index) => {
                    if (column.display === undefined || column.display === true) {
                        if (column.cellType != undefined) {
                            column.cell = CellFromType(column.cellType)
                            column.headerCell = HeaderCellFromType(column.cellType)
                        }
                        return (
                            <GridColumn
                                field={column.field}
                                title={column.title}
                                key={index}
                                className={column.className}
                                cell={column.cell}
                                headerCell={column.headerCell}
                                headerClassName={column.headerClassName}
                                sortable={column.sortable === undefined ? true : column.sortable}
                                format={column.format}
                                width={column.width}
                                filterable={column.filterable === undefined ? true : column.filterable}
                                filter={column.filterType}
                                filterCell={column.filterCell}
                                footerCell={column.footerCell}

                            />)
                    } else {
                        return (<></>)
                    }
                })
            }
        </Grid>

    )

    return (
        <>
            <div>
                {action || features.simpleSearch || features.title ?
                    <Row className='my-2 d-print-none'>
                        <Col>
                            <div className='gridTitle'>
                                {features.title && <h3 className=''>{features.title}</h3>}
                            </div>
                            <div className='gridActions'>
                                <div className='d-column d-md-grid d-md-flex justify-content-start justify-content-md-end'>
                                    {action ? <Actions /> : <></>}
                                    {features.simpleSearch ? <SimpleSearch /> : <></>}
                                </div>
                            </div>
                        </Col>
                    </Row> : <></>
                }

                {getData && !loaded ? (
                    <Skeleton shape='rectangle' style={{ width: '100%', height: '500px' }} animation={{ type: 'pulse' }} />
                ) : (
                    <>
                        {features.exportToExcel ? (
                            <ExcelExport ref={_export}>
                                {grid}
                            </ExcelExport>
                        ) : (
                            grid
                        )}
                        {features.exportToPDF && (
                            <GridPDFExport ref={(pdfExport) => (gridPDFExport = pdfExport)}>
                                {grid}
                            </GridPDFExport>
                        )}
                    </>
                )}

                {/* Modals */}
                {/* Single Delete */}
                <ConfirmModal
                    show={showDeleteConfirm}
                    errors={deleteErrors}
                    loading={deleteLoading}
                    title='Delete Confirmation'
                    content={
                        <>
                            {deleteAction?.getDeleteMessage &&
                                <div className='heehee'>
                                    {deleteAction.getDeleteMessage(itemToDelete)}
                                </div>
                            }
                        </>
                    }
                    onCancel={(e) => deleteModalCancelled(itemToDelete!)}
                    onSave={(e) => deleteModalConfirmed(e, itemToDelete!)} />
            </div>
        </>
    )
})

SAGrid.defaultProps = {
    features: {
        pageable: false,
        sortable: false,
        filterable: false,
        exportToPDF: false,
        exportToExcel: false,
        reorderColumns: false,
        resizable: true,
    }

}

export default SAGrid
